/**
 * Default `fetch`-based KanbanApi implementation (thin orchestrator).
 *
 * Tenants that use a different HTTP client (axios, ky, etc.) can swap this
 * out by writing their own implementation of `KanbanApi`.
 *
 * Decomposed under the operator 500-line ceiling: the shared HTTP plumbing
 * lives in `api-fetch/context` and the KanbanApi methods are grouped into
 * verbatim domain factories under `api-fetch/*`. This file only builds the
 * context and composes the factories. The public API
 * (createKanbanFetchApi + KanbanFetchApiOptions + KanbanAllocationConflictError)
 * is unchanged and re-exported below for back-compat with existing importers.
 */
import type { KanbanApi } from './api'
import {
  createApiFetchContext,
  KanbanAllocationConflictError,
  type KanbanFetchApiOptions,
} from './api-fetch/context'
import { createAccessLocksApi } from './api-fetch/access-locks'
import { createBoardsApi } from './api-fetch/boards'
import { createCardAttachmentsApi } from './api-fetch/card-attachments'
import { createCardsApi } from './api-fetch/cards'
import { createProgressiveSnapshotApi } from './api-fetch/progressive-snapshot'
import { createTrelloTeamsApi } from './api-fetch/trello-teams'
import { createMeetingsApi } from './api-fetch/meetings'
import { createWorkflowApi } from './api-fetch/workflow'
import { createTemplatesApi } from './api-fetch/templates'

export { KanbanAllocationConflictError }
export type { KanbanFetchApiOptions }

export function createKanbanFetchApi(opts: KanbanFetchApiOptions): KanbanApi {
  const ctx = createApiFetchContext(opts)
  return {
    ...createBoardsApi(ctx),
    // Progressive snapshot is opt-in until BE /progressive-snapshot/ is live.
    // Default wiring caused console 404 spam on every board open (BUG-2026-07-16).
    ...(opts.enableProgressiveSnapshot ? createProgressiveSnapshotApi(ctx) : {}),
    ...createCardsApi(ctx),
    ...createCardAttachmentsApi(ctx),
    ...createTrelloTeamsApi(ctx),
    ...createMeetingsApi(ctx),
    ...createWorkflowApi(ctx),
    ...createTemplatesApi(ctx),
    ...createAccessLocksApi(ctx),
  }
}
